import { Trans, useTranslation } from 'react-i18next'
import { useCallback, useState } from 'react'
import { Alert, Button } from '@/common'
import { Link } from 'react-router-dom'
import { useRequestResetPassword } from '@/features/auth/hooks/auth'
import toast from 'react-hot-toast'
import AuthLayout from '../../components/AuthLayout'
import EmailInputGroup from '@/shared/components/EmailInputGroup'
import { validateForgotPasswordForm } from './ForgotPasswordForm.schema'

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [success, setSuccess] = useState(false)
  const { mutateAsync: requestResetPassword, isPending } = useRequestResetPassword()

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setSuccess(false)

      const validationResult = validateForgotPasswordForm({ email })

      if (!validationResult.success) {
        // Validation errors are shown inline via EmailInputGroup
        return
      }

      try {
        await requestResetPassword({ email })
        setSuccess(true)
        toast.success(t('forgotPassword.notifications.success'))
      } catch (error) {
        toast.error(t('forgotPassword.notifications.error'))
        console.error(error)
      }
    },
    [email, requestResetPassword, t]
  )

  return (
    <AuthLayout showCarousel={false}>
      <div className="flex flex-col items-center space-y-4 p-6">
        <div className="text-center">
          <p>{t('forgotPassword.description')}</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 w-full mb-8">
          {success && (
            <Alert type="success" className="whitespace-pre-line">
              {t('forgotPassword.success')}
            </Alert>
          )}
          <EmailInputGroup
            onChange={evt => setEmail(evt.target.value)}
            value={email}
            disabled={success}
            name="email"
            label={t('forgotPassword.fields.email.label')}
          />
          <Button
            type="submit"
            className="w-full"
            isLoading={isPending}
            disabled={isPending || success}
          >
            {t('forgotPassword.actions.send')}
          </Button>
        </form>
        <div className="text-sm px-10 text-center">
          <Trans
            i18nKey="forgotPassword.backToLogin"
            components={{
              Link: <Link className="text-green-600 underline" to="/login" />
            }}
          />
        </div>
      </div>
    </AuthLayout>
  )
}
